import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addCart, delCart } from "../redux/action";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import toast from "react-hot-toast";
import api from "../utils/base_url";
import { Navbar, Footer } from "../components";
import CategorySidebar from "../components/CategorySidebar";
import '../styles/index.css';
import '../styles/CategoryProducts.css';

const CategoryProducts = () => {
    const { categoryId } = useParams();
    const [products, setProducts] = useState([]);
    const [categoryName, setCategoryName] = useState("");
    const [loading, setLoading] = useState(false);
    const [favorites, setFavorites] = useState([]);
    const [animatingHeart, setAnimatingHeart] = useState(null);
    const [stockError, setStockError] = useState({});
    const [disabledQty, setDisabledQty] = useState({});
    const [categories, setCategories] = useState([]);
    const [selectedCat, setSelectedCat] = useState(Number(categoryId));

    const { user } = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const state = useSelector((state) => state.handleCart);

    const getCartQty = (productId) => {
        const item = state.find((p) => p.id === productId);
        return item ? item.qty : 0;
    };

    const fetchCategories = useCallback(async () => {
        try {
            const res = await api.get("categorylist/");
            setCategories(res.data.data || []);
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    }, []);

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`productfilter/${categoryId}/`);
            const data = res.data.data || [];
            data.sort((b, a) => a.id - b.id);
            setProducts(data);
            if (data.length > 0) setCategoryName(data[0].category_name || "");
        } catch (error) {
            console.error("Error fetching products:", error);
        } finally {
            setLoading(false);
        }
    }, [categoryId]);

    const fetchFavorites = useCallback(async () => {
        if (!user) return;
        try {
            const res = await api.get(`favorites/ids/?auth_id=${user.id}`);
            setFavorites(res.data.favorites || []);
        } catch (err) {
            console.error(err);
        }
    }, [user]);

    useEffect(() => {
        fetchProducts();
        fetchFavorites();
        fetchCategories();
    }, [fetchProducts, fetchFavorites, fetchCategories]);

    useEffect(() => {
        setSelectedCat(Number(categoryId));
    }, [categoryId]);

    const toggleFavorite = async (productId) => {
        if (!user) {
            toast.error("Please login first!");
            navigate("/login");
            return;
        }
        try {
            const res = await api.post("favorites/toggle/", {
                product_id: productId,
                auth_id: user.id,
            });
            const isFav = res.data.favorite;
            setFavorites((prev) =>
                isFav ? [...prev, productId] : prev.filter((id) => id !== productId)
            );
            setAnimatingHeart(productId);
            setTimeout(() => setAnimatingHeart(null), 400);
            fetchFavorites();
        } catch (err) {
            toast.error("Failed, try again");
        }
    };

    const addItem = (product) => {
        const available = product.available_stock ?? product.stock_quantity;
        const nextQty = getCartQty(product.id) + 1;
        if (nextQty > available) {
            setStockError((prev) => ({ ...prev, [product.id]: true }));
            setDisabledQty((prev) => ({ ...prev, [product.id]: true }));
            setTimeout(() => {
                setStockError((prev) => ({ ...prev, [product.id]: false }));
                setDisabledQty((prev) => ({ ...prev, [product.id]: false }));
            }, 2000);
            return;
        }
        dispatch(addCart(product));
    };

    const removeItem = (product) => {
        dispatch(delCart(product));
        setStockError((prev) => {
            const s = { ...prev };
            delete s[product.id];
            return s;
        });
    };
    const Loading = () => (
        <div className="cp-grid">
            {[...Array(6)].map((_, idx) => (
                <div key={idx} className="cp-skeleton-card">
                    <Skeleton height={140} className="cp-skeleton-rounded" />
                    <Skeleton height={20} width={120} className="cp-skeleton-top" />
                    <Skeleton height={20} width={90} className="cp-skeleton-top" />
                    <Skeleton height={35} width="60%" className="cp-skeleton-center" />
                </div>
            ))}
        </div>
    );

    return (
        <>
            <Navbar />
            <div className="cp-page-bg">
                <div className="d-flex align-items-start cp-wrapper">

                    {/* Sidebar */}
                    <div className="cp-sidebar-follow">
                        <CategorySidebar
                            categories={categories}
                            selectedCat={selectedCat}
                            handleCategory={(id) => {
                                navigate(`/category/${id}`);
                            }}
                        />
                    </div>

                    {/* Main Content */}
                    <div className="flex-grow-1 py-4 px-3">

                        {/* Back button + Title */}
                        <div className="d-flex align-items-center gap-3 mb-4">
                            <button
                                onClick={() => navigate("/product")}
                                className="cp-back-btn"
                            >
                                ← Back
                            </button>
                            <h2 className="fw-bold text-uppercase mb-0 cp-title">
                                {categoryName || "Products"}
                            </h2>
                        </div>

                        {loading ? (
                            <Loading />
                        ) : products.length === 0 ? (
                            <div className="text-center py-5 cp-empty-state">
                                <h5>No products available in this category.</h5>
                                <button
                                    onClick={() => navigate("/products")}
                                    className="cp-browse-btn mt-3"
                                >
                                    Browse All Products
                                </button>
                            </div>

                        ) : (
                            <div className="cp-grid">
                                {products.map((product, idx) => {
                                    const hasOffer =
                                        product.offer_percentage &&
                                        Number(product.offer_percentage) > 0 &&
                                        product.offer_price;
                                    const available = product.available_stock ?? product.stock_quantity;
                                    const outOfStock = available === 0;
                                    const stockLow = available <= 2 && available > 0;

                                    return (
                                        <div
                                            key={product.id}
                                            className={`cp-card ${outOfStock ? "cp-out-stock-card" : "cp-clickable"}`}
                                            onClick={() => { if (!outOfStock) navigate(`/product/${product.id}`); }}
                                        >
                                            {hasOffer && (
                                                <span className="cp-offer-badge">
                                                    {Math.round(product.offer_percentage)}% OFF
                                                </span>
                                            )}
                                            {outOfStock && (
                                                <span className="cp-stock-badge cp-out-stock">Out of Stock</span>
                                            )}
                                            {!outOfStock && stockLow && (
                                                <span className="cp-stock-badge cp-low-stock">
                                                    Hurry! Only {available} left
                                                </span>
                                            )}
                                            {stockError[product.id] && (
                                                <motion.span
                                                    initial={{ scale: 0.8, opacity: 0 }}
                                                    animate={{ scale: [0.8, 1.05, 1], opacity: 1 }}
                                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                                    className="cp-stock-error-badge"
                                                >
                                                    Stock limit reached!
                                                </motion.span>
                                            )}

                                            {/* Image */}
                                            <div className="cp-img-wrapper">
                                                <img src={product.product_image} alt={product.product_name} />
                                            </div>

                                            {/* Content */}
                                            <div className="cp-content">
                                                <div className="cp-title-text">
                                                    {product.product_name.length > 15
                                                        ? product.product_name.substring(0, 15) + "..."
                                                        : product.product_name}
                                                </div>

                                                {/* Favorite */}
                                                <motion.div
                                                    onClick={(e) => { e.stopPropagation(); toggleFavorite(product.id); }}
                                                    initial={{ scale: 1 }}
                                                    whileTap={{ scale: 0.9 }}
                                                    className="cp-heart-wrap"
                                                >
                                                    {user && (
                                                        <div className="cp-heart-btn">
                                                            {favorites.includes(product.id) ? (
                                                                <motion.span
                                                                    animate={animatingHeart === product.id ? { scale: [1, 1.4, 1] } : {}}
                                                                    transition={{ duration: 0.3 }}
                                                                    className="cp-heart-icon"
                                                                >❤️</motion.span>
                                                            ) : (
                                                                <span className="cp-heart-icon">🤍</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </motion.div>
                                                <p className="cp-description">{product.product_description}</p>

                                                {/* Price */}
                                                <div className="cp-price-wrap">
                                                    {hasOffer ? (
                                                        <>
                                                            <span className="cp-price-offer">
                                                                ₹ {parseFloat(product.offer_price).toLocaleString()}
                                                            </span>
                                                            <span className="cp-price-original">
                                                                <s>₹ {parseFloat(product.price).toLocaleString()}</s>
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="cp-price">
                                                            ₹ {parseFloat(product.price).toLocaleString()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Buttons */}
                                            <div className="cp-btns" onClick={(e) => e.stopPropagation()}>
                                                {product.stock_quantity === 0 ? (
                                                    <span className="cp-out-stock-label">Out of Stock</span>
                                                ) : getCartQty(product.id) === 0 ? (
                                                    <button className="cp-btn-buy" onClick={() => addItem(product)}>
                                                        Add to Cart
                                                    </button>
                                                ) : (
                                                    <div className="cp-qty-box">
                                                        <button className="cp-qty-btn" onClick={() => removeItem(product)}>
                                                            <i className="fas fa-minus"></i>
                                                        </button>
                                                        <span className="cp-qty-value">{getCartQty(product.id)}</span>
                                                        <button
                                                            className="cp-qty-btn"
                                                            onClick={() => addItem(product)}
                                                            disabled={disabledQty[product.id]}
                                                        >
                                                            <i className="fas fa-plus"></i>
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        {/* 🔥 Bottom Back Button */}
                        <div className="cp-bottom-back-wrap">
                            <button
                                onClick={() => navigate("/product")}
                                className="cp-back-btn"
                            >
                                ← Back to Other Products
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default CategoryProducts;